import { useEffect, useState, lazy, Suspense } from 'react';
import { Loader2, Mail, Sparkles, UserCheck } from 'lucide-react';
import { ErrorBoundary } from 'react-error-boundary';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { safeLog } from '@/utils/safeLogger';
import { Button } from '@/components/ui/button';
import { AccessDenied } from './AccessDenied';
import { ThemeProvider } from "next-themes";

// Lazy load AddInTaskPane for performance
const LazyAddInTaskPane = lazy(() => 
  import('./AddInTaskPane').then(m => ({ default: m.AddInTaskPane }))
);

const OfficeInitLoading = () => (
  <div className="min-h-screen flex flex-col items-center justify-center gap-3 p-4">
    <Loader2 className="w-8 h-8 text-primary animate-spin" />
    <p className="text-sm text-muted-foreground">Initializing Office Add-In...</p>
  </div>
);

const TaskPaneLoading = () => (
  <div className="min-h-screen flex flex-col items-center justify-center gap-3 p-4">
    <Loader2 className="w-6 h-6 text-primary animate-spin" />
    <p className="text-sm text-muted-foreground">Loading add-in...</p>
  </div>
);

const ErrorFallback = ({ error, resetErrorBoundary }: any) => {
  const { user } = useAuth();
  
  return (
    <div className="min-h-screen flex flex-col items-center justify-center gap-4 p-4">
      <div className="text-center">
        <h2 className="text-lg font-semibold text-destructive mb-2">Something went wrong</h2>
        <p className="text-sm text-muted-foreground mb-4">{error?.message || 'An unexpected error occurred'}</p>
        {user && <p className="text-xs text-muted-foreground">Logged in as: {user.email}</p>}
      </div>
      <Button onClick={resetErrorBoundary}>
        Try Again
      </Button>
    </div>
  );
};

const LoginPrompt = ({ onLoginClick }: { onLoginClick: () => void }) => (
  <div className="min-h-screen flex flex-col items-center justify-center gap-6 p-6 bg-background">
    <div className="text-center max-w-sm flex flex-col gap-3">
      <div className="mx-auto mb-1 w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center">
        <Mail className="h-7 w-7 text-primary" aria-hidden="true" />
      </div>
      <h2 className="font-display text-xl font-semibold text-foreground tracking-tight">
        Welcome to email2contact
      </h2>
      <p className="text-sm text-muted-foreground leading-relaxed">
        Turn any email signature into a saved Outlook contact in seconds.
      </p>
    </div>

    <ul className="w-full max-w-sm space-y-2.5" aria-label="Features">
      <li className="flex items-start gap-3 rounded-lg bg-muted/40 p-3">
        <Mail className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" aria-hidden="true" />
        <span className="text-xs text-foreground leading-relaxed">
          <span className="font-medium">Extract</span> contact details from any email signature.
        </span>
      </li>
      <li className="flex items-start gap-3 rounded-lg bg-muted/40 p-3">
        <Sparkles className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" aria-hidden="true" />
        <span className="text-xs text-foreground leading-relaxed">
          <span className="font-medium">Enrich</span> with AI-powered company research.
        </span>
      </li>
      <li className="flex items-start gap-3 rounded-lg bg-muted/40 p-3">
        <UserCheck className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" aria-hidden="true" />
        <span className="text-xs text-foreground leading-relaxed">
          <span className="font-medium">Save</span> directly to your Outlook contacts.
        </span>
      </li>
    </ul>

    <Button
      onClick={onLoginClick}
      size="lg"
      className="w-full max-w-sm gap-2"
    >
      <svg className="h-4 w-4" viewBox="0 0 23 23" aria-hidden="true">
        <rect x="1" y="1" width="10" height="10" fill="#F25022" />
        <rect x="12" y="1" width="10" height="10" fill="#7FBA00" />
        <rect x="1" y="12" width="10" height="10" fill="#00A4EF" />
        <rect x="12" y="12" width="10" height="10" fill="#FFB900" />
      </svg>
      Sign in with Microsoft
    </Button>

    <p className="text-[11px] text-muted-foreground text-center max-w-sm">
      By signing in, you agree to our{' '}
      <a href="/terms" target="_blank" rel="noopener noreferrer" className="underline hover:text-foreground">
        Terms
      </a>{' '}
      and{' '}
      <a href="/privacy" target="_blank" rel="noopener noreferrer" className="underline hover:text-foreground">
        Privacy Policy
      </a>
      .
    </p>
  </div>
);

export const AddInWrapper = () => {
  const [isOfficeReady, setIsOfficeReady] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [dialogInstance, setDialogInstance] = useState<Office.Dialog | null>(null);
  const { user, session, loading } = useAuth();
  const [sessionCheckComplete, setSessionCheckComplete] = useState(false);

  // Log auth state for debugging (no PII)
  useEffect(() => {
    safeLog.dev('🔐 AddInWrapper Auth State:', {
      hasUser: !!user,
      hasSession: !!session,
      hasAccessToken: !!session?.access_token,
      loading,
      sessionCheckComplete
    });
  }, [user, session, loading, sessionCheckComplete]);

  // Session retry logic - wait briefly and recheck if no session after loading completes
  useEffect(() => {
    // If loading is done but no session, wait and recheck
    if (!loading && !session && !sessionCheckComplete) {
      const timer = setTimeout(async () => {
        safeLog.info('🔄 AddInWrapper: Rechecking session...');
        const { data } = await supabase.auth.getSession();
        if (data.session) {
          safeLog.info('✅ AddInWrapper: Session found on retry');
        } else {
          safeLog.info('🔒 AddInWrapper: No session found on retry');
        }
        setSessionCheckComplete(true);
      }, 500);
      return () => clearTimeout(timer);
    }
    // If we have a session, mark check as complete
    if (!loading && session) {
      setSessionCheckComplete(true);
    }
  }, [loading, session, sessionCheckComplete]);


  useEffect(() => {
    safeLog.info('=== ADD-IN WRAPPER MOUNTED ===');
    
    if ((window as any).__OFFICE_READY__) {
      safeLog.info('✅ Office already ready (from index.html)');
      
      const Office = (window as any).Office;
      if (!Office?.context?.mailbox) {
        safeLog.error('❌ NOT IN OUTLOOK CONTEXT - Access Denied');
        (window as any).__ACCESS_DENIED__ = true;
        setIsOfficeReady(false);
        return;
      }
      
      setIsOfficeReady(true);
      return;
    }
    
    let timeoutId: ReturnType<typeof setTimeout>;
    
    if (typeof (window as any).Office !== 'undefined') {
      safeLog.info('🔄 Calling Office.onReady() from AddInWrapper...');
      (window as any).Office.onReady((info: any) => {
        safeLog.info(`✅ Office.js ready (from AddInWrapper): ${info?.host}`);
        
        const Office = (window as any).Office;
        if (!Office?.context?.mailbox) {
          safeLog.error('❌ NOT IN OUTLOOK CONTEXT - Access Denied');
          (window as any).__ACCESS_DENIED__ = true;
          setIsOfficeReady(false);
          return;
        }
        
        setIsOfficeReady(true);
      });

      timeoutId = setTimeout(() => {
        safeLog.warn('⚠️ Office.onReady timeout - forcing ready state');
        setIsOfficeReady(true);
      }, 3000);
    } else {
      safeLog.error('❌ Office.js not available');
      setIsOfficeReady(true);
    }

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, []);

  const openLoginDialog = () => {
    const Office = (window as any).Office;
    
    if (!Office?.context?.mailbox) {
      safeLog.error('❌ Not in Outlook context - cannot open auth dialog');
      return;
    }
    
    if (!Office?.context?.ui) {
      safeLog.warn('❌ Office.context.ui not available - cannot open dialog');
      return;
    }

    const loginUrl = `${window.location.origin}/auth-start-microsoft?mode=dialog`;
    safeLog.info('🔓 Opening login dialog:', { loginUrl });
    
    Office.context.ui.displayDialogAsync(
      loginUrl,
      { height: 85, width: 65, promptBeforeOpen: true },
      (asyncResult: Office.UIResult) => {
        if (asyncResult.status === Office.AsyncResultStatus.Succeeded) {
          const dialog = asyncResult.value;
          setDialogInstance(dialog);
          setIsDialogOpen(true);
          safeLog.info('✅ Login dialog opened');
          
          dialog.addEventHandler(
            Office.EventType.DialogMessageReceived,
            async (arg: any) => {
              // Validate message exists and is a string
              if (!arg?.message || typeof arg.message !== 'string') {
                safeLog.info('[AddInWrapper] Ignoring non-string message');
                return;
              }

              // Only parse if it looks like JSON
              if (!arg.message.startsWith('{')) {
                safeLog.info('[AddInWrapper] Ignoring non-JSON message');
                return;
              }

              try {
                const message = JSON.parse(arg.message);
                
                // Accept both modern (type: 'auth-success') and legacy (status: 'success') formats
                const isAuthSuccess = message.type === 'auth-success' || message.status === 'success';
                
                if (isAuthSuccess && message.session) {
                  safeLog.info('✅ Login successful, received session from dialog');
                  
                  if (message.session?.access_token && message.session?.refresh_token) {
                    safeLog.info('💾 Setting session in add-in from dialog...');
                    
                    try {
                      await supabase.auth.setSession({
                        access_token: message.session.access_token,
                        refresh_token: message.session.refresh_token
                      });
                      
                      safeLog.info('✅ Session set successfully!');
                      
                      message.session.access_token = '';
                      message.session.refresh_token = '';
                      message.session = null;
                      
                      // No reload - setSession triggers onAuthStateChange which updates AuthContext
                    } catch (error) {
                      safeLog.error('❌ Failed to set session', error as Error);
                    }
                  } else {
                    safeLog.warn('⚠️ No session data in message from dialog');
                  }
                  
                  dialog.close();
                  setIsDialogOpen(false);
                  setDialogInstance(null);
                  
                  // Reset session check to trigger re-evaluation via React context
                  setSessionCheckComplete(false);
                } else {
                  safeLog.info('[AddInWrapper] Ignoring unhandled message type');
                }
              } catch (error) {
                // Only log parsing errors, don't show to user
                safeLog.warn('[AddInWrapper] Failed to parse message as JSON');
              }
            }
          );

          dialog.addEventHandler(
            Office.EventType.DialogEventReceived,
            (arg: any) => {
              safeLog.info('Dialog event:', arg);
              setIsDialogOpen(false);
              setDialogInstance(null);
            }
          );
        } else {
          safeLog.error('❌ Dialog open failed:', asyncResult.error as any);
          setIsDialogOpen(false);
        }
      }
    );
  };


  if ((window as any).__ACCESS_DENIED__) {
    return <AccessDenied />;
  }

  if (!isOfficeReady) {
    return <OfficeInitLoading />;
  }

  // Wait for both auth loading AND session retry check to complete
  if (loading || !sessionCheckComplete) {
    return <TaskPaneLoading />;
  }

  // Trust Supabase SDK to handle token refresh - simplified validation
  const hasValidSession = !!user && !!session?.access_token;

  if (!hasValidSession) {
    safeLog.info('🔒 AddInWrapper: No valid session - showing login prompt');
    return <LoginPrompt onLoginClick={openLoginDialog} />;
  }

  safeLog.dev('✅ AddInWrapper: User authenticated');

  return (
    <ThemeProvider attribute="class" defaultTheme="system" enableSystem>
      <ErrorBoundary
        FallbackComponent={ErrorFallback}
        onError={(error) => safeLog.error('[AddIn Error]', error)}
        onReset={() => window.location.reload()}
      >
        <Suspense fallback={<TaskPaneLoading />}>
          <LazyAddInTaskPane />
        </Suspense>
      </ErrorBoundary>
    </ThemeProvider>
  );
};
